import {
    FormLayout,
    Form,
    TextField,
    Button,
    Select,
    LegacyStack,
    Toast,
    Frame, Checkbox, DropZone, Text, Banner, List
} from '@shopify/polaris';
import { useState, useCallback, useEffect } from 'react';
import Api from '../../helpers/api';
import {cloneDeep} from 'lodash';
import {CountrySelector, StateSelector} from "../Selectors";
import {filterShippingMethods, getFormError} from "../../helpers/utils";
import {getValue} from "../Helpers";

const CustomerForm = props => {

    const [loading, setLoading] = useState(false);
    const [allShippingMethods, setAllShippingMethods] = useState({});
    const [shippingMethods, setShippingMethods] = useState([]);

    useEffect(() => {
        Api.getShippingMethods().then(results => {
            setAllShippingMethods(results.data);
        })
    }, [])

    const emptyCustomer = () => {
        return {
            'status': 'approved',  // Internal customers are just approved.
            'full_name': '',
            'email': '',
            'shipping_method': null,
            'credit_limit': '',
            'discount': '',
            'notes': '',
            'proof_1': null,
            'proof_2': null,
            'proof_3': null,
            'proof_4': null,
            'state_sales_tax_resale': '',
            'business_license': '',
            'preferred_carrier': '',
            'airport_code': '',
            'years_at_current_address': '',
            'years_in_business': '',
            'alt_phone_number': '',
            'cell_phone': '',
            'home_phone': '',
            'alt_contact_names': '',
            'contact_person': '',
            'websites': '',
            'alt_phone': '',
            'fax': '',
            'company_name': '',
            'referred_by': '',
            'contact_name': '',
            'types_of_business': [],
            'is_on_hold': false,
            credit_enabled: false,
            credit_balance: 0,

            ship_to: {
                address1: '',
                address2: '',
                city: '',
                zip_code: '',
                country: '',
                state: '',
            },
            bill_to: {
                address1: '',
                address2: '',
                city: '',
                zip_code: '',
                country: '',
                state: '',
            },
        }
    };

    const existingCustomer = () => {
        return {
            ...props.customer,
            ...props.customer.additional_data,
            shipping_method: props.customer?.shipping_method ? {
                name: props.customer.shipping_method?.name ?? '',
                price: props.customer.shipping_method?.price ?? '0',
                key: props.customer.shipping_method?.shopify_rate_id ?? props.customer.shipping_method?.key //fallback for old values
            } : null,
            id: props.customer.id,
            status: props.customer.status,
            credit_enabled: props.customer.credit_enabled ?? false,
            credit_balance: props.customer.credit_balance ?? 0,

            cell_phone: getValue(props.customer.cell_phone, props.customer.metafields?.cell_phone) ?? '',
            fax: getValue(props.customer.fax, props.customer.metafields?.fax) ?? '',
        }
    }

    const [toastActive, setToastActive] = useState(false);
    const [form, setForm] = useState((props.customer && props.updating)
        ? existingCustomer()
        : emptyCustomer() );

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const address = props.updating
            ? form.default_location?.shipping
            : form.location;

        if (allShippingMethods && (allShippingMethods.data || allShippingMethods.custom))
            setShippingMethods(filterShippingMethods(address?.country, address?.state, allShippingMethods));
    }, [allShippingMethods, form]);

    const handleInputChange = useCallback((value, id) => {
        const payload = {
            [id]: value,
        };

        setForm({
            ...form,
            ...payload,
        });
    }, [form]);

    const handleAddressChange = useCallback((value, id) => {
        let keys = id.split('.');
        let clonedForm = cloneDeep(form);
        clonedForm[keys[0]][keys[1]] = value;
        setForm(clonedForm);
    }, [form]);

    const handleShippingMethodChange = useCallback((key) => {
        let clonedForm = cloneDeep(form);

        if (!key || key.length == 0) {
            clonedForm.shipping_method = {
                name: '',
                price: '',
                key: '',
            }
            setForm(clonedForm);
            return;
        }

        let method = null;
        shippingMethods.forEach((item) => {
            if (item.key == key)
                method = item;
        });

        if (!method) {
            clonedForm.shipping_method = {
                name: '',
                price: '',
                key: '',
            }
            setForm(clonedForm);
            return;
        }

        clonedForm.shipping_method = {
            name: method.name,
            price: method.price,
            key: method.key,
        }
        setForm(clonedForm);
    }, [form, shippingMethods]);

    const changeCustomShippingMethod = useCallback((value, id) => {
        let clonedForm = cloneDeep(form);
        clonedForm.shipping_method[id] = value;
        setForm(clonedForm);
    }, [form]);

    const handleSubmit = () => {
        setLoading(true);
        setErrors({});

        Api.upsertCustomer(form)
            .then (({ data }) => {
                props.onCustomerCreated(data.data);
                setLoading(false);
            })
            .catch ( error => {
                toggleToast();
                setLoading(false);

                if (error.response.status == 422) {
                    setErrors(error.response.data.errors);
                } else {
                    throw error;
                }
            });
    }

    const showError = name => getFormError(errors, name);

    const toggleToast = useCallback(() => setToastActive((toastActive) => !toastActive), []);

    const toastMarkup = toastActive ? (
      <Toast content="There has been an error processing the form." error onDismiss={toggleToast} />
    ) : null;

    const handleBusinessTypesChange = useCallback((val, type) => {
        const typeOfBusinesses = form.types_of_business;

        if (val && typeOfBusinesses.includes(type)) {
            typeOfBusinesses.reject((el) => el == type)
        } else if (val) {
            typeOfBusinesses.push(type)
        }

        setForm({
            ...form,
            types_of_business: typeOfBusinesses,
        })
    }, [form]);

    const fileUpload = (file) => !file && (
        <DropZone.FileUpload actionHint="" />
    );

    const getFileStack = (file) => file && (
        <LegacyStack>
            <div>
                {file.name}{' '}
                <Text as={'p'}>
                    {file.size} bytes
                </Text>
            </div>
        </LegacyStack>
    );
    const handleDropZoneDrop = useCallback(
        (file, id) => {
            setForm({
                ... form,
                [id]: file
            })
        },
        [form]
    );

    return (
        <Frame>
            <Form onSubmit={handleSubmit}>
            <FormLayout>
                <FormLayout.Group>
                    <LegacyStack vertical>
                        <Select label="Default Shipping Method"
                                options={[
                                    { label: '--Select Shipping Method--', value: '' },
                                    ...(shippingMethods.map((method) => {
                                        return {
                                            label: method.label,
                                            value: method.key + ''
                                        }
                                    }))
                                ]}
                                onChange={handleShippingMethodChange}
                                value={form.shipping_method?.key + ''}
                                id="shipping_method"
                                error={showError('shipping_method')}
                        />

                        { form.shipping_method?.key == 'custom' && (
                            <LegacyStack distribution={"fillEvenly"}>
                                <TextField autoComplete={'on'} label="Shipping Method Name" id="name" value={form.shipping_method.name} onChange={changeCustomShippingMethod} error={showError('shipping_method.name')} />
                                <TextField autoComplete={'on'} label="Shipping Method Price" id="price" type={"number"} min={0} step={1.0} value={form.shipping_method.price.toString()} onChange={changeCustomShippingMethod} error={showError('shipping_method.price')} />
                            </LegacyStack>
                        ) }
                    </LegacyStack>
                </FormLayout.Group>
                <hr />
                <FormLayout.Group>
                    <Checkbox
                        checked={form.credit_enabled}
                        onChange={handleInputChange}
                        label="Credit Enabled"
                        id="credit_enabled"
                        error={showError('credit_enabled')}
                        helpText={form.credit_expires_at ? `Credit expires at ${form.credit_expires_at}` : null}
                    />
                    <TextField
                        autoComplete={'off'}
                        value={(form.credit_enabled ? form.credit_balance : 0) + ''}
                        onChange={handleInputChange}
                        disabled={!form.credit_enabled}
                        label="Credit Balance"
                        type="number"
                        id="credit_balance"
                        error={showError('credit_balance')}
                    />
                </FormLayout.Group>
                <FormLayout.Group>
                        <TextField autoComplete={'off'} value={form.credit_limit} onChange={handleInputChange} label="Credit Limit" type="text" id="credit_limit" error={showError('credit_limit')} />
                        <TextField autoComplete={'off'} value={form.discount} onChange={handleInputChange} label="Discount (%)" type="text" id="discount" error={showError('discount')} />
                </FormLayout.Group>

                <hr />
                <Text variant="headingSm" as="h4">Owners Information</Text>
                <FormLayout.Group>
                    <LegacyStack vertical>
                        <TextField autoComplete={'on'} label="Name" value={form.full_name} onChange={handleInputChange} type="text" id="full_name" error={showError('full_name')} />
                        <TextField autoComplete={'on'} label="Email" value={form.email} onChange={handleInputChange} type="email" id="email" error={showError('email')} />

                        {!form?.id && (<>
                            <TextField autoComplete={'on'} label="Address 1" requiredIndicator value={form.bill_to?.address1} onChange={handleAddressChange} type="text" id="bill_to.address1" error={showError('bill_to.address1')} />
                            <TextField autoComplete={'on'} label="Address 2" value={form.bill_to?.address2} onChange={handleAddressChange} type="text" id="bill_to.address2" error={showError('bill_to.address2')} />
                            <CountrySelector onChange={(val) => {handleAddressChange(val, "bill_to.country")}}
                                             value={form.bill_to?.country} error={showError('bill_to.country')} />
                            <StateSelector onChange={(val) => {handleAddressChange(val, "bill_to.state")}}
                                           value={form.bill_to?.state} error={showError('bill_to.state')} selectedCountry={form.bill_to?.country} />
                            <TextField autoComplete={'on'} label="City" requiredIndicator value={form.bill_to?.city} onChange={handleAddressChange} type="text" id="bill_to.city" error={showError('bill_to.city')} />
                            <TextField autoComplete={'on'} label="Zip Code" requiredIndicator value={form.bill_to?.zip_code} onChange={handleAddressChange} type="text" id="bill_to.zip_code" error={showError('bill_to.zip_code')} />
                        </>)}

                        <TextField autoComplete={'on'} label="Home Phone" value={form.home_phone} onChange={handleInputChange} type="tel" id="home_phone" error={showError('home_phone')} />
                        <TextField autoComplete={'on'} label="Cell Phone" value={form.cell_phone} onChange={handleInputChange} type="tel" id="cell_phone" error={showError('cell_phone')} />
                    </LegacyStack>
                </FormLayout.Group>

                <Text variant="headingSm" as="h4">Business Information <Text as={'p'} variation={"negative"}>*</Text></Text>
                <FormLayout.Group>
                    <LegacyStack vertical>
                        { !!errors.types_of_business && (<Text as={'p'} variation={"negative"}>{showError('types_of_business')}</Text> )}
                        <LegacyStack>
                            <Checkbox error={!!errors.types_of_business} label={"Retail Store"} checked={form.types_of_business.includes("retail_store")} onChange={(val) => handleBusinessTypesChange(val, 'retail_store')} />
                            <Checkbox error={!!errors.types_of_business} label={"Online Retailer"} checked={form.types_of_business.includes("online_retailer")} onChange={(val) => handleBusinessTypesChange(val, 'online_retailer')} />
                            <Checkbox error={!!errors.types_of_business} label={"Service Maintenance Business"} checked={form.types_of_business.includes("service_maintenance_business")} onChange={(val) => handleBusinessTypesChange(val, 'service_maintenance_business')} />
                            <Checkbox error={!!errors.types_of_business} label={"Educational Institution"} checked={form.types_of_business.includes("educational_institution")} onChange={(val) => handleBusinessTypesChange(val, 'educational_institution')} />
                            <Checkbox error={!!errors.types_of_business} label={"Public Aquarium/Zoo"} checked={form.types_of_business.includes("public_aquarium_zoo")} onChange={(val) => handleBusinessTypesChange(val, 'public_aquarium_zoo')} />
                        </LegacyStack>
                    </LegacyStack>
                </FormLayout.Group>
                <FormLayout.Group>
                    <LegacyStack vertical>
                        <TextField autoComplete={'on'} label="Contact Name" value={form.contact_name} onChange={handleInputChange} type="text" id="contact_name" error={showError('contact_name')} />
                        <TextField autoComplete={'on'} label="Reffered By" value={form.referred_by} onChange={handleInputChange} type="text" id="referred_by" error={showError('referred_by')} />
                        <TextField autoComplete={'on'} label="Business Name" requiredIndicator value={form.company_name} onChange={handleInputChange} type="text" id="company_name" error={showError('company_name')} />

                        {!form?.id && (<>
                            <TextField autoComplete={'on'} label="Address 1" requiredIndicator value={form.ship_to?.address1} onChange={handleAddressChange} type="text" id="ship_to.address1" error={showError('ship_to.address1')} />
                            <TextField autoComplete={'on'} label="Address 2" value={form.ship_to?.address2} onChange={handleAddressChange} type="text" id="ship_to.address2" error={showError('ship_to.address2')} />
                            <CountrySelector onChange={(val) => {handleAddressChange(val, "ship_to.country")}}
                                             value={form.ship_to?.country} error={showError('ship_to.country')} />
                            <StateSelector onChange={(val) => {handleAddressChange(val, "ship_to.state")}}
                                           value={form.ship_to?.state} error={showError('ship_to.state')} selectedCountry={form.ship_to?.country} />
                            <TextField autoComplete={'on'} label="City" requiredIndicator value={form.ship_to?.city} onChange={handleAddressChange} type="text" id="ship_to.city" error={showError('ship_to.city')} />
                            <TextField autoComplete={'on'} label="Zip Code" requiredIndicator value={form.ship_to?.zip_code} onChange={handleAddressChange} type="text" id="ship_to.zip_code" error={showError('ship_to.zip_code')} />
                        </>)}

                        <TextField autoComplete={'on'} label="Fax" value={form.fax} onChange={handleInputChange} type="text" id="fax" error={showError('fax')} />
                        <TextField autoComplete={'on'} label="Alt. Phone" value={form.alt_phone} onChange={handleInputChange} type="phone" id="alt_phone" error={showError('alt_phone')} />
                        <TextField autoComplete={'on'} label="Websites" value={form.websites} onChange={handleInputChange} type="text" id="websites" error={showError('websites')} />
                        <TextField autoComplete={'on'} label="Contact Person(s)" value={form.contact_person} onChange={handleInputChange} type="text" id="contact_person" error={showError('contact_person')} />
                        <TextField autoComplete={'on'} label="Alt. Contact Name(s)" value={form.alt_contact_names} onChange={handleInputChange} type="text" id="alt_contact_names" error={showError('alt_contact_names')} />
                        <TextField autoComplete={'on'} label="Alt. Phone No" value={form.alt_phone_number} onChange={handleInputChange} type="tel" id="alt_phone_number" error={showError('alt_phone_number')} />
                        <TextField autoComplete={'on'} label="Years in Business" value={form.years_in_business} onChange={handleInputChange} type="text" id="years_in_business" error={showError('years_in_business')} />
                        <TextField autoComplete={'on'} label="Years at Current Address" value={form.years_at_current_address} onChange={handleInputChange} type="text" id="years_at_current_address" error={showError('years_at_current_address')} />
                        <TextField autoComplete={'on'} label="Airport Code" value={form.airport_code} onChange={handleInputChange} type="text" id="airport_code" error={showError('airport_code')} />
                    </LegacyStack>
                </FormLayout.Group>
                <FormLayout.Group>
                    <LegacyStack vertical>
                        <TextField autoComplete={'on'} requiredIndicator={!form.id && form.ship_to?.state == 'CA'} label="State Sales Tax Resale #" value={form.state_sales_tax_resale} onChange={handleInputChange} type="text" id="state_sales_tax_resale" error={showError('state_sales_tax_resale')} />
                        <TextField autoComplete={'on'} requiredIndicator label="Business License or Federal Tax ID" value={form.business_license} onChange={handleInputChange} type="text" id="business_license" error={showError('business_license')} />
                    </LegacyStack>
                </FormLayout.Group>

                {props.creating && (
                    <>
                        <hr />
                        <Text variant="headingMd" as="h2">Location</Text>
                        <div style={{marginLeft: '-2rem'}}>
                            <FormLayout.Group>
                                <Checkbox
                                    checked={form.location?.credit_enabled ?? false}
                                    onChange={handleAddressChange}
                                    label="Credit Enabled"
                                    id="location.credit_enabled"
                                    error={showError('location.credit_enabled')}
                                />
                                <TextField
                                    autoComplete={'off'}
                                    value={((form.location?.credit_enabled ?? false) ? form.location?.credit_balance : 0) + ''}
                                    onChange={handleInputChange}
                                    disabled={!(form.location?.credit_enabled ?? false)}
                                    label="Credit Balance"
                                    type="number"
                                    id="location.credit_balance"
                                    error={showError('location.credit_balance')}
                                />
                            </FormLayout.Group>
                        </div>
                    </>
                )}

                <hr />
                <FormLayout.Group>
                    <TextField autoComplete={'off'} label="Notes" value={form.notes} onChange={handleInputChange} type="text" id="notes" error={showError('notes')} multiline={4} />
                </FormLayout.Group>

                <Button loading={loading} submit  variant="primary">Submit</Button>
            </FormLayout>
            </Form>
            {toastMarkup}
        </Frame>
    );
}

export default CustomerForm;
